import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Logo from '../../assets/icons/logo.svg';
import { CartIcon } from '../../assets/icons';
import '../../assets/css/layout/header.css';
import ShoppingCart from '../../components/store/ShoppingCart';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { ToastContainer } from 'react-toastify';
import RoleSwitcher from '../common/RoleSwitcher';
// import VisitorStats from '../elements/visitorStats';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { getCartCount, setShowCart, showCart, cartItems } = useCart();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const userMenuRef = useRef(null);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        await logout();
        setIsMenuOpen(false);
        navigate('/login');
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
    };

    const handleCartClick = () => {
        setShowCart(true);
        setIsMenuOpen(false);
    };

    return (
        <header className="header">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />

            <div className="header-container">
                {/* Logo Section */}
                <div className="logo-section">
                    <Link to="/">
                        <img src={Logo} alt="E-AquaHealth Logo" className="logo" />
                    </Link>
                </div>

                {/* Desktop Navigation */}
                <nav className="desktop-nav">
                    <Link to="/" className={location.pathname === '/' ? 'active' : ''}>Home</Link>
                    <Link to="/about" className={location.pathname === '/about' ? 'active' : ''}>About</Link>
                    <Link to="/store" className={location.pathname === '/store' ? 'active' : ''}>Store</Link>
                    <Link to="/aqua-doc" className={location.pathname === '/aqua-doc' ? 'active' : ''}>AquaDoc</Link>
                    <Link to="/learning" className={location.pathname === '/learning' ? 'active' : ''}>Learning</Link>
                </nav>

                <div className='right-nav'>
                    {/* Role Switcher for users with multiple roles */}
                    {isAuthenticated && <RoleSwitcher />}
                    
                    <Link to="/cart" className="cart-icon-wrapper">
                        <img src={CartIcon} alt="Shopping Cart" className="cart-icon" />
                        {getCartCount() > 0 && (
                            <span className="cart-count">{getCartCount()}</span>
                        )}
                    </Link>

                    {/* Desktop Auth Buttons */}
                    <div className="auth-buttons desktop-auth">
                        {isAuthenticated ? (
                            <div className="user-menu-wrapper" ref={userMenuRef}>
                                <button 
                                    className="user-menu-trigger"
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                >
                                    <div className="user-avatar">
                                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                                    </div>
                                    <div className="user-info">
                                        <span className="user-name">{user?.first_name} {user?.last_name}</span>
                                        <span className="user-email">{user?.email}</span>
                                    </div>
                                    <span className={`arrow ${showUserMenu ? 'up' : 'down'}`}></span>
                                </button>
                                
                                {showUserMenu && (
                                    <div className="user-menu">
                                        <div className="user-menu-header">
                                            <span className="user-fullname">{user?.first_name} {user?.last_name}</span>
                                            <span className="user-role">{user?.role || 'User'}</span>
                                        </div>
                                        <div className="menu-divider"></div>
                                        <Link to="/profile" onClick={() => setShowUserMenu(false)}>
                                            <i className="icon-profile"></i>My Profile
                                        </Link>
                                        <Link to="/store/orders" onClick={() => setShowUserMenu(false)}>
                                            <i className="icon-orders"></i>My Orders
                                        </Link>
                                        <Link to="/messages" onClick={() => setShowUserMenu(false)}>
                                            <i className="icon-message"></i>Messages
                                        </Link>
                                        <Link to="/cart" onClick={() => setShowUserMenu(false)}>
                                            <i className="icon-cart"></i>My Cart
                                        </Link>
                                        <Link to="/change-password" onClick={() => setShowUserMenu(false)}>
                                            <i className="icon-password"></i>Change Password
                                        </Link>
                                        <div className="menu-divider"></div>
                                        <button onClick={handleLogout} className="logout-btn">
                                            <i className="icon-logout"></i>Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <Link to="/login" className="btn-login">Login</Link>
                                <Link to="/register" className="btn-register">Register</Link>
                            </>
                        )}
                    </div>

                    {/* Mobile Hamburger */}
                    <button className="hamburger" onClick={toggleMenu} aria-label="Toggle menu">
                        <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
                    </button>
                </div>

                {/* Mobile Menu Overlay */}
                <div className={`mobile-overlay ${isMenuOpen ? 'open' : ''}`} onClick={closeMenu}></div>

                {/* Mobile Menu */}
                <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
                    {/* Mobile Menu Header */}
                    <div className="mobile-menu-header">
                        <Link to="/" onClick={closeMenu}>
                            <img src={Logo} alt="E-AquaHealth" className="mobile-logo" />
                        </Link>
                        <button className="close-menu-btn" onClick={closeMenu} aria-label="Close menu">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    {/* Mobile User Section */}
                    {isAuthenticated ? (
                        <div className="mobile-user-section">
                            <div className="mobile-user-avatar">
                                {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                            </div>
                            <div className="mobile-user-info">
                                <span className="mobile-user-name">{user?.first_name} {user?.last_name}</span>
                                <span className="mobile-user-email">{user?.email}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="mobile-auth-section">
                            <Link to="/login" className="mobile-btn-login" onClick={closeMenu}>Login</Link>
                            <Link to="/register" className="mobile-btn-register" onClick={closeMenu}>Register</Link>
                        </div>
                    )}

                    {/* Mobile Navigation Links */}
                    <nav className="mobile-nav">
                        <Link to="/" onClick={closeMenu} className={location.pathname === '/' ? 'active' : ''}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                            Home
                        </Link>
                        <Link to="/about" onClick={closeMenu} className={location.pathname === '/about' ? 'active' : ''}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                            About Us
                        </Link>
                        <Link to="/store" onClick={closeMenu} className={location.pathname === '/store' ? 'active' : ''}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <path d="M16 10a4 4 0 0 1-8 0"></path>
                            </svg>
                            Store
                            {getCartCount() > 0 && <span className="mobile-badge">{getCartCount()}</span>}
                        </Link>
                        <Link to="/aqua-doc" onClick={closeMenu} className={location.pathname === '/aqua-doc' ? 'active' : ''}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                            </svg>
                            AquaDoc AI
                        </Link>
                        <Link to="/learning" onClick={closeMenu} className={location.pathname === '/learning' ? 'active' : ''}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                            </svg>
                            Learning Hub
                        </Link>
                    </nav>

                    {/* Divider */}
                    <div className="mobile-divider"></div>

                    {/* Mobile Quick Actions */}
                    <div className="mobile-quick-actions">
                        <button onClick={handleCartClick} className="mobile-cart-btn">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="9" cy="21" r="1"></circle>
                                <circle cx="20" cy="21" r="1"></circle>
                                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                            </svg>
                            Shopping Cart
                            {getCartCount() > 0 && <span className="mobile-badge">{getCartCount()}</span>}
                        </button>
                    </div>

                    {/* Authenticated User Links */}
                    {isAuthenticated && (
                        <>
                            <div className="mobile-divider"></div>
                            <div className="mobile-user-links">
                                <Link to="/profile" onClick={closeMenu}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="12" cy="7" r="4"></circle>
                                    </svg>
                                    My Profile
                                </Link>
                                <Link to="/orders" onClick={closeMenu}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                        <line x1="1" y1="10" x2="23" y2="10"></line>
                                    </svg>
                                    My Orders
                                </Link>
                                <Link to="/messages" onClick={closeMenu}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                    </svg>
                                    Messages
                                </Link>
                                <Link to="/change-password" onClick={closeMenu}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    </svg>
                                    Change Password
                                </Link>
                            </div>
                            <div className="mobile-divider"></div>
                            <div className="mobile-logout-section">
                                <button onClick={handleLogout} className="mobile-logout-btn">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                    Logout
                                </button>
                            </div>
                        </>
                    )}

                    {/* Mobile Footer */}
                    <div className="mobile-menu-footer">
                        <p>© 2026 E-AquaHealth</p>
                        <div className="mobile-footer-links">
                            <Link to="/privacy" onClick={closeMenu}>Privacy</Link>
                            <Link to="/terms" onClick={closeMenu}>Terms</Link>
                            <Link to="/contact" onClick={closeMenu}>Contact</Link>
                        </div>
                    </div>
                </div>
            </div>

            {showCart && (
                <ShoppingCart 
                    items={cartItems}
                    onClose={() => setShowCart(false)}
                />
            )}
        </header>
    );
};

export default Header;